<template>
  <div class="tab-panel-content">
    <div class="station-layout">
      <div class="station-list">
        <div class="search">
          <el-input v-model="codeStationSearch" placeholder="搜索电站..." clearable size="small" prefix-icon="Search" />
        </div>
        <div class="items">
          <div
            v-for="s in filteredCodeStations"
            :key="s.id"
            class="s-item"
            :class="{ active: isCodeStationSelected(s) }"
            @click="toggleCodeStation(s)"
          >
            <span class="mdi mdi-office-building-outline s-icon" style="color: var(--primary)"></span>
            <span class="s-name">{{ s.name }}</span>
            <span class="s-count">{{ codeEnabledCount(s) }}/{{ allAlarmCodes.length }}</span>
          </div>
        </div>
      </div>
      <div v-if="selectedCodeStationIds.length" class="station-detail">
        <v-progress-linear v-if="codeLoading" indeterminate color="primary" style="height: 2px;"/>
        <div v-else style="height: 2px;"></div>
        <div class="sd-header">
          <div class="sd-title">
            {{ selectedCodeStations[0].name }} <small>ID: {{ selectedCodeStations[0].id }}</small>
          </div>
          <div style="display: flex; gap: 6px">
            <v-btn size="small" variant="flat" color="success" plain :disabled="!canEditStationSwitch" @click="handleToggleAllCodes(true)">全开</v-btn>
            <v-btn size="small" variant="flat" color="info" plain :disabled="!canEditStationSwitch" @click="handleToggleAllCodes(false)">全关</v-btn>
            <!-- <el-button size="small" type="primary" @click="saveStationCodeConfig">保存</el-button> -->
          </div>
        </div>
        <div class="sd-body">
          <div v-for="grp in deviceGroups" :key="grp.key" class="alarm-cat-group">
            <div class="acg-head">
              <span class="mdi grp-icon" :class="grp.icon"></span>
              <span class="acg-name">{{ grp.name }}</span>
              <span class="acg-count">{{ codeGroupEnabledCount(grp.key) }}/{{ codeGroupAlarms(grp.key).length }}</span>
            </div>
            <div
              v-for="code in codeGroupAlarms(grp.key)"
              :key="code.code"
              class="alarm-row"
              :class="{ on: curCodes[code.id], off: !curCodes[code.id] }"
            >
              <div class="ar-info">
                <div class="ar-name">{{ code.description }}</div>
                <div class="ar-desc">{{ code.details }}</div>
              </div>
              <span class="ar-code">{{ code.alarmCode }}</span>
              <span  class="ar-st" :class="curCodes[code.id] ? 'on' : 'off'">
                {{ curCodes[code.id] ? '已开启' : '已关闭' }}
              </span>
              <div class="ar-switch">
                <span class="ar-switch-label">客户展示</span>
                <v-switch
                  :model-value="curCodes[code.id]"
                  :loading="savingCodeIds.has(String(code.id))"
                  :disabled="!canEditStationSwitch"
                  density="compact"
                  hide-details
                  color="primary"
                  @update:model-value="(v) => handleToggleCodeAndSave(code, v)"
                />
              </div>
              <div class="ar-switch">
                <span class="ar-switch-label">客户推送</span>
                <v-switch
                  :model-value="curPushCodes[code.id]"
                  :loading="savingPushIds.has(String(code.id))"
                  :disabled="!canEditStationSwitch"
                  density="compact"
                  hide-details
                  color="primary"
                  @update:model-value="(v) => handleTogglePushAndSave(code, v)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="station-detail station-detail-empty">
        <span class="mdi mdi-arrow-left" style="margin-right: 6px"></span> 请先勾选左侧电站
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAlarmConfig } from '../useAlarmConfig'
import { checkPermi } from '@/utils/permission'
import { computed } from 'vue'

const {
  deviceGroups,
  allAlarmCodes,
  codeGroupAlarms,
  codeStationSearch,
  selectedCodeStationIds,
  selectedCodeStations,
  curCodes,
  curPushCodes,
  mixedCodes,
  codeLoading,
  savingCodeIds,
  savingPushIds,
  filteredCodeStations,
  toggleCodeStation,
  isCodeStationSelected,
  codeEnabledCount,
  codeGroupEnabledCount,
  onToggleCode,
  onToggleCodeAndSave,
  onTogglePushAndSave,
  toggleAllCodes,
  saveStationCodeConfig,
  fetchAlarmCodesFromServer,
} = useAlarmConfig()

const canEditStationSwitch = computed(() => checkPermi(['ops:alarmConfig:stationSwitch']))

function handleToggleAllCodes(enabled) {
  if (!canEditStationSwitch.value) return
  toggleAllCodes(enabled)
}

function handleToggleCodeAndSave(code, enabled) {
  if (!canEditStationSwitch.value) return
  onToggleCodeAndSave(code, enabled)
}

function handleTogglePushAndSave(code, enabled) {
  if (!canEditStationSwitch.value) return
  onTogglePushAndSave(code, enabled)
}

fetchAlarmCodesFromServer()


</script>

<style scoped lang="scss">
.station-layout {
  display: flex;
  gap: 16px;
  min-height: 480px;
}

.station-list {
  width: 260px;
  flex-shrink: 0;
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid #fff;
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  backdrop-filter: blur(6px);
}

.station-list .search {
  padding: 12px;
  border-bottom: 1px solid var(--color-td-border);
}

.station-list .items {
  flex: 1;
  overflow-y: auto;
  max-height: 420px;
}

.s-item {
  padding: 11px 14px;
  cursor: pointer;
  border-bottom: 1px solid var(--color-td-border);
  transition: background 0.15s ease;
  display: flex;
  align-items: center;
  gap: 10px;
}

.s-item:hover {
  background: var(--primary-faint);
}

.s-item.active {
  background: var(--primary-light);
  border-left: 3px solid var(--primary);
}

.s-item .s-icon {
  font-size: 18px;
}

.s-item .s-name {
  font-size: 13px;
  color: var(--text-primary);
  flex: 1;
}

.s-item .s-count {
  font-size: 11px;
  padding: 1px 8px;
  border-radius: 8px;
  background: var(--surface-muted);
  color: var(--text-muted);
}

.s-item.active .s-count {
  background: color-mix(in srgb, var(--color-primary) 18%, #e8eefc);
  color: var(--primary);
}

.station-detail {
  flex: 1;
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid #fff;
  box-shadow: var(--shadow-card);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  backdrop-filter: blur(6px);
}

.station-detail-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
}

.sd-header {
  padding: 14px 20px;
  border-bottom: 1px solid var(--color-td-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sd-header .sd-title {
  font-size: 15px;
  font-weight: 600;
}

.sd-header .sd-title small {
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 400;
  margin-left: 6px;
}

.sd-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  font-size: 12px;
  color: var(--primary);
  background: var(--primary-faint);
  border-bottom: 1px solid var(--color-td-border);
}

.sd-tip .mdi {
  font-size: 15px;
  flex-shrink: 0;
}

.sd-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  max-height: 420px;
}

.alarm-cat-group {
  margin-bottom: 20px;
}

.alarm-cat-group .acg-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-td-border);
}

.acg-head .grp-icon {
  font-size: 16px;
  color: var(--primary);
  flex-shrink: 0;
}

.alarm-cat-group .acg-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.alarm-cat-group .acg-count {
  font-size: 12px;
  color: var(--text-muted);
}

.alarm-row {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-radius: 10px;
  margin-bottom: 6px;
  border: 1px solid transparent;
  transition:
    background 0.2s ease,
    border-color 0.2s ease;
}

.alarm-row:hover {
  background: color-mix(in srgb, var(--color-text) 4%, #ffffff);
}

.alarm-row.on {
  background: color-mix(in srgb, var(--color-success) 10%, #ffffff);
  border-color: color-mix(in srgb, var(--color-success) 32%, #ffffff);
}

.alarm-row.off {
  background: color-mix(in srgb, var(--color-info) 8%, #ffffff);
  border-color: color-mix(in srgb, var(--color-info) 22%, #ffffff);
}

.alarm-row.mixed {
  background: color-mix(in srgb, var(--color-warning) 10%, #ffffff);
  border-color: color-mix(in srgb, var(--color-warning) 30%, #ffffff);
}

.alarm-row .ar-info {
  flex: 1;
}

.alarm-row .ar-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
}

.alarm-row .ar-desc {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 2px;
}

.alarm-row .ar-code {
  font-size: 11px;
  color: var(--text-muted);
  font-family: monospace;
  margin-right: 14px;
}

.alarm-row .ar-st {
  font-size: 12px;
  margin-right: 10px;
}

/* 两个开关（客户展示 / 客户推送）各带文字标签，固定等宽保证跨行对齐 */
.alarm-row .ar-switch {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  width: 108px;
  justify-content: flex-end;
}

.alarm-row .ar-switch + .ar-switch {
  margin-left: 8px;
}

.alarm-row .ar-switch-label {
  font-size: 12px;
  color: var(--text-muted);
  white-space: nowrap;
}

.alarm-row .ar-switch :deep(.v-switch) {
  flex: none;
}

.alarm-row .ar-st.on {
  color: var(--success);
}

.alarm-row .ar-st.off {
  color: var(--info);
}

.alarm-row .ar-st.mixed {
  color: var(--warning);
  font-weight: 600;
}

@media (prefers-reduced-motion: reduce) {
  .alarm-row,
  .s-item {
    transition: none;
  }
}

@media (max-width: 960px) {
  .station-layout {
    flex-direction: column;
  }

  .station-list {
    width: 100%;
    max-height: 280px;
  }
}
</style>
